// About.js - Improved with better styling and fixed background sections
import { useEffect, useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import { TrendingUp, Shield, Clock, Award, Target, Users, BarChart3, CheckCircle } from 'lucide-react';

export default function About() {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, amount: 0.3 });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15, // Reduced from 0.2
        duration: 0.6 // Reduced from 0.8
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5, // Reduced from 0.6
        ease: "easeOut"
      }
    }
  };

  const features = [
    {
      icon: TrendingUp,
      title: 'Growth Focused',
      description: 'Strategic financing solutions that grow with your business and investment portfolio'
    },
    {
      icon: Shield,
      title: 'Trusted & Secure',
      description: 'Licensed professionals committed to protecting your interests and financial security'
    },
    {
      icon: Clock,
      title: 'Fast Approvals',
      description: 'Streamlined process with quick turnaround times to close deals faster'
    },
    {
      icon: Award,
      title: 'Expert Guidance',
      description: 'Years of experience navigating complex commercial mortgage landscapes'
    }
  ];

  const missionStats = [
    { number: '15+', label: 'Years Experience' },
    { number: '500+', label: 'Projects Completed' },
    { number: '$2B+', label: 'Total Portfolio Value' },
    { number: '24/7', label: 'Client Support' }
  ];

  const missionPrinciples = [
    {
      icon: Target,
      title: 'Client-Centric Approach',
      description: 'We put your business goals first, ensuring every financing solution aligns with your long-term vision and immediate needs.'
    },
    {
      icon: Users,
      title: 'Relationship Building',
      description: 'Building lasting partnerships with clients and lenders to create sustainable success stories across Canada.'
    },
    {
      icon: BarChart3,
      title: 'Market Intelligence',
      description: 'Leveraging deep market knowledge and analytics to secure optimal terms in any economic climate.'
    },
    {
      icon: CheckCircle,
      title: 'Excellence in Execution',
      description: 'Meticulous attention to detail and proactive communication throughout the entire financing process.'
    }
  ];

  return (
    <>
      {/* Main About Section */}
      <section id="about" ref={sectionRef} className="py-20 bg-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-emerald-50 rounded-full -translate-y-1/2 translate-x-1/2"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-blue-50 rounded-full translate-y-1/2 -translate-x-1/2"></div>

        <motion.div 
          className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative"
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
        >
          <motion.div variants={itemVariants} className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              Why Choose Done Commercials?
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              We specialize in commercial financing, offering personalized solutions
              for investors, developers, and business owners across Canada
            </p>
          </motion.div>

          <motion.div variants={containerVariants} className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-20">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={index}
                  variants={itemVariants}
                  className="group p-8 bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 border border-slate-100 hover:border-emerald-200"
                  whileHover={{ 
                    scale: 1.02,
                    y: -5,
                    transition: { duration: 0.2 } // Reduced from 0.3
                  }}
                >
                  <div className="w-16 h-16 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-200 shadow-lg">
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-slate-600 leading-relaxed">
                    {feature.description}
                  </p>
                </motion.div>
              );
            })}
          </motion.div>

          {/* Enhanced Mission Section */}
          <motion.div 
            variants={itemVariants}
            className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-3xl p-8 md:p-12 text-white mb-16"
            whileInView={{ 
              opacity: 1, 
              y: 0,
              transition: { duration: 0.6, delay: 0.2 } // Reduced timing
            }}
            initial={{ opacity: 0, y: 50 }}
          >
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h3 className="text-3xl md:text-4xl font-bold mb-6">
                  Our Mission & Commitment
                </h3>
                <p className="text-lg text-slate-300 mb-6 leading-relaxed">
                  At Done Commercials, we are committed to revolutionizing commercial financing 
                  by providing transparent, innovative, and personalized solutions that empower businesses 
                  to grow and thrive in today's competitive landscape.
                </p>
                <p className="text-lg text-slate-300 mb-8 leading-relaxed">
                  Our team of experienced professionals works tirelessly to secure the best financing options 
                  for your unique needs, leveraging our extensive network of lenders and deep industry expertise 
                  to deliver exceptional results.
                </p>
                
                <motion.div 
                  className="grid grid-cols-2 gap-6 mt-8"
                  variants={containerVariants}
                >
                  {missionStats.map((stat, index) => (
                    <motion.div 
                      key={index} 
                      variants={itemVariants}
                      className="text-center"
                      whileHover={{ scale: 1.05 }}
                    >
                      <div className="text-2xl md:text-3xl font-bold text-emerald-400 mb-2">
                        {stat.number}
                      </div>
                      <div className="text-slate-400 text-sm">
                        {stat.label}
                      </div>
                    </motion.div>
                  ))}
                </motion.div>
              </div>

              <motion.div 
                className="grid grid-cols-2 gap-6"
                variants={containerVariants}
              >
                {missionPrinciples.map((principle, index) => {
                  const Icon = principle.icon;
                  return (
                    <motion.div
                      key={index}
                      variants={itemVariants}
                      className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20 hover:border-emerald-400/30 transition-all duration-200"
                      whileHover={{ 
                        y: -5,
                        transition: { duration: 0.2 } // Reduced from 0.3
                      }}
                    >
                      <div className="w-12 h-12 bg-emerald-500/20 rounded-xl flex items-center justify-center mb-4">
                        <Icon className="w-6 h-6 text-emerald-400" />
                      </div>
                      <h4 className="font-semibold text-white mb-3">
                        {principle.title}
                      </h4>
                      <p className="text-slate-300 text-sm leading-relaxed">
                        {principle.description}
                      </p>
                    </motion.div>
                  );
                })}
              </motion.div>
            </div>
          </motion.div>
        </motion.div>
      </section>

      {/* Fixed Background Image Section */}
      <div className="h-screen bg-fixed bg-cover bg-center" style={{
        backgroundImage: 'url("https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80")'
      }}>
        <div className="h-full bg-black/40 flex items-center justify-center">
          <div className="text-center text-white px-4">
            <h3 className="text-4xl md:text-5xl font-bold mb-6">Building Canada's Future</h3>
            <p className="text-xl md:text-2xl max-w-2xl mx-auto">
              One successful commercial financing project at a time
            </p>
          </div>
        </div>
      </div>

      {/* Enhanced Value Proposition Section */}
      <section className="py-20 bg-gradient-to-br from-slate-50 to-white">
        <motion.div 
          className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <div className="bg-gradient-to-r from-emerald-500 to-teal-600 rounded-3xl p-8 md:p-12 text-white text-center shadow-2xl">
            <h3 className="text-3xl md:text-4xl font-bold mb-6">
              Building Your Success Story
            </h3>
            <p className="text-lg md:text-xl mb-8 leading-relaxed max-w-4xl mx-auto">
              We believe that successful commercial financing is about more than just numbers—it's about 
              understanding your vision, anticipating market trends, and creating opportunities that 
              drive sustainable growth. Our comprehensive approach ensures that every financing solution 
              we deliver is tailored to your specific goals and positioned for long-term success.
            </p>
            
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <div className="text-center">
                <div className="text-3xl font-bold mb-2">15+</div>
                <div className="text-emerald-100">Overall Experience</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold mb-2">$2B+</div>
                <div className="text-emerald-100">Portfolio Value</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold mb-2">98%</div>
                <div className="text-emerald-100">Success Rate</div>
              </div>
            </div>

            <motion.button
              onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
              className="bg-white text-emerald-600 px-8 py-4 rounded-lg font-bold hover:bg-gray-100 transform hover:scale-105 transition-all duration-300 shadow-lg"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Start Your Success Story
            </motion.button>
          </div>
        </motion.div>
      </section>
    </>
  );
}